/**
 * Motor Perceptual de Color para Ambilight (Perceptual Color Engine)
 * 
 * Basado en la arquitectura:
 * AMBI LIGHT COLOR = SPACE × PERCEPTION × CONTEXT × TIME
 * 
 * - Espacio de color uniforme OKLab / OKLCH
 * - Kernel espacial con mayor peso hacia el borde físico del LED
 * - Detección automática y exclusión de barras negras (letterbox / pillarbox)
 * - Contraste local y saliencia perceptual (objetos pequeños destacados como estrellas)
 * - Separación de movimiento global (paneos de cámara) vs local (explosiones, fuego)
 * - Detección heurística de subtítulos (TextLikelihoodMap) para evitar secuestro de color blanco
 * - Detección de elementos persistentes de HUD / UI
 * - Cobertura sublineal (C^α) para balancear fondo vs acentos
 * - Scoring perceptual y mezcla Softmax con temperatura (τ)
 * - Detección de cortes de escena (scene cuts) y filtro temporal adaptativo
 */

import type { AlgorithmTune, RGB, ZoneId } from '../types/ambilight';

// ===== 1. Espacio de Color Perceptual OKLab & OKLCH =====

export interface OKLab {
  L: number; // [0, 1] Luminancia perceptual
  a: number; // Eje verde (-) a rojo (+)
  b: number; // Eje azul (-) a amarillo (+)
  chroma: number; // Cromaticidad
  hue: number;    // Tono en radianes
}

// Tabla de consulta (LUT) para srgbToLinear: 0 nanosegundos vs Math.pow(x, 2.4)
const SRGB_TO_LINEAR_LUT = new Float32Array(256);
for (let i = 0; i < 256; i++) {
  const v = i / 255;
  SRGB_TO_LINEAR_LUT[i] = v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
}

export function srgbToLinear(c: number): number {
  return SRGB_TO_LINEAR_LUT[Math.min(255, Math.max(0, Math.round(c)))];
}

export function linearToSrgb(c: number): number {
  const v = Math.max(0, Math.min(1, c));
  const s = v <= 0.0031308 ? v * 12.92 : 1.055 * Math.pow(v, 1 / 2.4) - 0.055;
  return Math.round(s * 255);
}

export function rgbToOklab(r: number, g: number, b: number): OKLab {
  const lr = SRGB_TO_LINEAR_LUT[r & 0xff];
  const lg = SRGB_TO_LINEAR_LUT[g & 0xff];
  const lb = SRGB_TO_LINEAR_LUT[b & 0xff];

  const l = 0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb;
  const m = 0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb;
  const s = 0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb;

  const l_ = Math.cbrt(l);
  const m_ = Math.cbrt(m);
  const s_ = Math.cbrt(s);

  const L = 0.2104542553 * l_ + 0.7936177850 * m_ - 0.0040720468 * s_;
  const a = 1.9779984951 * l_ - 2.4285922050 * m_ + 0.4505937099 * s_;
  const bVal = 0.0259040371 * l_ + 0.7827717662 * m_ - 0.8086757660 * s_;

  const chroma = Math.sqrt(a * a + bVal * bVal);
  const hue = Math.atan2(bVal, a);

  return { L, a, b: bVal, chroma, hue };
}

export function oklabToRgb(L: number, a: number, bVal: number): RGB {
  const l_ = L + 0.3963377774 * a + 0.2158037573 * bVal;
  const m_ = L - 0.1055613458 * a - 0.0638541728 * bVal;
  const s_ = L - 0.0894841775 * a - 1.2914855480 * bVal;

  const l = l_ * l_ * l_;
  const m = m_ * m_ * m_;
  const s = s_ * s_ * s_;

  const lr = +4.0767434721 * l - 3.3077115913 * m + 0.2309699292 * s;
  const lg = -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s;
  const lb = -0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s;

  return {
    r: linearToSrgb(lr),
    g: linearToSrgb(lg),
    b: linearToSrgb(lb),
  };
}

// ===== 2. Memoria Temporal Perceptual por Zona =====

export interface PerceptualZoneMemory {
  prevGridL: Float32Array | null;
  prevGridA: Float32Array | null;
  prevGridB: Float32Array | null;
  gridW: number;
  gridH: number;
  prevHist: Float32Array | null;
  outputR: number;
  outputG: number;
  outputB: number;
  outputL: number;
  targetR: number;
  targetG: number;
  targetB: number;
  velocity: number;
  framesActive: number;
  staticBlocksCount: Uint16Array | null;
}

const zoneMemories: Map<string, PerceptualZoneMemory> = new Map();

export function getPerceptualMemory(zoneKey: string): PerceptualZoneMemory {
  let mem = zoneMemories.get(zoneKey);
  if (!mem) {
    mem = {
      prevGridL: null,
      prevGridA: null,
      prevGridB: null,
      gridW: 0,
      gridH: 0,
      prevHist: null,
      outputR: 0,
      outputG: 0,
      outputB: 0,
      outputL: 0,
      targetR: 0,
      targetG: 0,
      targetB: 0,
      velocity: 0,
      framesActive: 0,
      staticBlocksCount: null,
    };
    zoneMemories.set(zoneKey, mem);
  }
  return mem;
}

// ===== 3. Kernel Espacial Según Posición Física del LED =====

export function computeSpatialWeight(
  u: number, // 0..1 en X de la zona
  v: number, // 0..1 en Y de la zona
  zoneId: ZoneId
): number {
  switch (zoneId) {
    case 'left':
      // El borde físico está a la izquierda (u=0)
      return 1.0 - 0.65 * Math.pow(u, 1.2);

    case 'right':
      // El borde físico está a la derecha (u=1)
      return 1.0 - 0.65 * Math.pow(1 - u, 1.2);

    case 'top':
      // El borde físico está arriba (v=0)
      return 1.0 - 0.65 * Math.pow(v, 1.2);

    case 'bottom':
      // El borde físico está abajo (v=1)
      return 1.0 - 0.65 * Math.pow(1 - v, 1.2);

    case 'top-left': {
      const d = Math.sqrt(u * u + v * v);
      return Math.max(0.3, 1.0 - 0.65 * (d / 1.414));
    }

    case 'top-right': {
      const du = 1 - u;
      const d = Math.sqrt(du * du + v * v);
      return Math.max(0.3, 1.0 - 0.65 * (d / 1.414));
    }

    case 'bottom-left': {
      const dv = 1 - v;
      const d = Math.sqrt(u * u + dv * dv);
      return Math.max(0.3, 1.0 - 0.65 * (d / 1.414));
    }

    case 'bottom-right': {
      const du = 1 - u;
      const dv = 1 - v;
      const d = Math.sqrt(du * du + dv * dv);
      return Math.max(0.3, 1.0 - 0.65 * (d / 1.414));
    }

    case 'center': {
      const cx = u - 0.5;
      const cy = v - 0.5;
      const distCenter = Math.sqrt(cx * cx + cy * cy);
      return 1.0 - 0.5 * (distCenter / 0.707);
    }

    case 'all':
    default:
      return 1.0;
  }
}

// ===== 4. Detección de Barras Negras (Letterbox / Pillarbox) =====

export function detectBlackBarFactor(
  v: number,
  zoneId: ZoneId,
  avgL: number
): number {
  // Si la zona es superior o inferior y se detectan franjas persistentes oscuras
  if ((zoneId === 'top' && v < 0.20) || (zoneId === 'bottom' && v > 0.80)) {
    if (avgL < 0.035) {
      return 0.05; // Fuerte penalización: es barra negra cinematográfica
    }
  }
  return 1.0;
}

// ===== 5. Generación de Candidatos de Color Perceptuales =====

interface ColorCandidate {
  oklab: OKLab;
  srgb: RGB;
  coverage: number;     // Fracción de píxeles
  saliency: number;     // [0, 1] Saliencia perceptiva
  luminance: number;    // [0, 1] Luminancia OKLab
  localContrast: number;// [0, 1] Contraste local
  motion: number;       // [0, 1] Movimiento
  spatialWeight: number;// Peso según cercanía física al LED
  blackBarFactor: number;
  textPenalty: number;  // Penalización si asemeja texto/subtítulo
  uiPenalty: number;    // Penalización si es HUD persistente
}

// Buffers preasignados de alto rendimiento para ZERO presión de Garbage Collection
const STATIC_MAX_CELLS = 256;
const STATIC_GRID_L = new Float32Array(STATIC_MAX_CELLS);
const STATIC_GRID_A = new Float32Array(STATIC_MAX_CELLS);
const STATIC_GRID_B = new Float32Array(STATIC_MAX_CELLS);
const STATIC_GRID_R = new Uint8Array(STATIC_MAX_CELLS);
const STATIC_GRID_G = new Uint8Array(STATIC_MAX_CELLS);
const STATIC_GRID_B_COL = new Uint8Array(STATIC_MAX_CELLS);
const STATIC_GRID_CONTRAST = new Float32Array(STATIC_MAX_CELLS);
const STATIC_GRID_MOTION = new Float32Array(STATIC_MAX_CELLS);
const STATIC_HIST = new Float32Array(16);

interface PreallocatedBin {
  count: number;
  sumL: number;
  sumA: number;
  sumB: number;
  sumContrast: number;
  sumMotion: number;
  sumSpatial: number;
  sumBlackBar: number;
  sumTextLikelihood: number;
  rSum: number;
  gSum: number;
  bSum: number;
}

const STATIC_CANDIDATE_BINS: PreallocatedBin[] = Array.from({ length: 8 }, () => ({
  count: 0,
  sumL: 0,
  sumA: 0,
  sumB: 0,
  sumContrast: 0,
  sumMotion: 0,
  sumSpatial: 0,
  sumBlackBar: 1.0,
  sumTextLikelihood: 0,
  rSum: 0,
  gSum: 0,
  bSum: 0,
}));

function resetStaticCandidateBins() {
  for (let i = 0; i < 8; i++) {
    const c = STATIC_CANDIDATE_BINS[i];
    c.count = 0;
    c.sumL = 0;
    c.sumA = 0;
    c.sumB = 0;
    c.sumContrast = 0;
    c.sumMotion = 0;
    c.sumSpatial = 0;
    c.sumBlackBar = 1.0;
    c.sumTextLikelihood = 0;
    c.rSum = 0;
    c.gSum = 0;
    c.bSum = 0;
  }
}

// ===== 6. Función Principal del Motor Perceptual (Optimizado a 60 FPS) =====

export function analyzePerceptualZone(
  data: Uint8ClampedArray,
  cw: number,
  ch: number,
  zoneId: ZoneId,
  tune: AlgorithmTune
): { rawColor: RGB; brightness: number; info?: string } {
  const n = cw * ch;
  if (n === 0) return { rawColor: { r: 0, g: 0, b: 0 }, brightness: 0 };

  const memKey = zoneId;
  const mem = getPerceptualMemory(memKey);

  // Perfil perceptual configurable
  const profile = tune.perceptualProfile || 'cinematic';
  let alphaCov = tune.perceptualCoverageAlpha ?? 0.35; // Exponente sublineal de cobertura
  let betaSal = tune.perceptualSaliencyWeight ?? 1.0;   // Peso de saliencia
  let deltaMot = tune.perceptualMotionWeight ?? 0.8;    // Peso de movimiento dinámico
  let lambdaText = tune.perceptualTextPenalty ?? 0.90; // Rechazo de subtítulos
  let tauTemp = 3.5;                                   // Temperatura Softmax

  if (profile === 'cinematic') {
    alphaCov = 0.42;
    betaSal = 0.85;
    deltaMot = 0.45;
    lambdaText = 0.95;
    tauTemp = 3.2;
  } else if (profile === 'reactive') {
    alphaCov = 0.28;
    betaSal = 1.35;
    deltaMot = 1.30;
    lambdaText = 0.70;
    tauTemp = 4.0;
  } else if (profile === 'game') {
    alphaCov = 0.30;
    betaSal = 1.20;
    deltaMot = 1.10;
    lambdaText = 0.85;
    tauTemp = 3.8;
  }

  // Muestreo en cuadrícula perceptual ultrarrápida (12x8 = 96 celdas, 60 FPS estables)
  const gw = Math.min(12, Math.max(4, Math.floor(cw / 8)));
  const gh = Math.min(8, Math.max(4, Math.floor(ch / 8)));
  const totalCells = gw * gh;

  const stepX = cw / gw;
  const stepY = ch / gh;

  let sumL = 0;
  let totalZoneMotion = 0;

  // Paso 1: Extracción en espacio OKLab y mapa espacial sin asignaciones
  for (let gy = 0; gy < gh; gy++) {
    const cy = Math.min(ch - 1, Math.floor((gy + 0.5) * stepY));
    for (let gx = 0; gx < gw; gx++) {
      const cx = Math.min(cw - 1, Math.floor((gx + 0.5) * stepX));
      const idx = (cy * cw + cx) * 4;
      const cellIdx = gy * gw + gx;

      const r = data[idx];
      const g = data[idx + 1];
      const b = data[idx + 2];

      const oklab = rgbToOklab(r, g, b);
      STATIC_GRID_L[cellIdx] = oklab.L;
      STATIC_GRID_A[cellIdx] = oklab.a;
      STATIC_GRID_B[cellIdx] = oklab.b;
      STATIC_GRID_R[cellIdx] = r;
      STATIC_GRID_G[cellIdx] = g;
      STATIC_GRID_B_COL[cellIdx] = b;

      sumL += oklab.L;

      // Movimiento contra frame previo
      if (mem.prevGridL && mem.gridW === gw && mem.gridH === gh) {
        const dL = Math.abs(oklab.L - mem.prevGridL[cellIdx]);
        const da = Math.abs(oklab.a - mem.prevGridA[cellIdx]);
        const db = Math.abs(oklab.b - mem.prevGridB[cellIdx]);
        const mVal = Math.min(1.0, dL * 1.5 + (da + db) * 0.8);
        STATIC_GRID_MOTION[cellIdx] = mVal;
        totalZoneMotion += mVal;
      } else {
        STATIC_GRID_MOTION[cellIdx] = 0;
      }
    }
  }

  const avgZoneL = sumL / totalCells;
  const avgZoneMotion = totalZoneMotion / totalCells;

  // Paso 2: Separar Movimiento Global vs Local
  let motionSpreadVariance = 0;
  for (let i = 0; i < totalCells; i++) {
    const dm = STATIC_GRID_MOTION[i] - avgZoneMotion;
    motionSpreadVariance += dm * dm;
  }
  const motionStdDev = Math.sqrt(motionSpreadVariance / totalCells);
  const isGlobalMotion = avgZoneMotion > 0.18 && motionStdDev / (avgZoneMotion + 1e-4) < 0.45;
  const effectiveMotionScale = isGlobalMotion ? 0.25 : 1.0;

  // Paso 3: Contraste Local instantáneo sin bucles anidados
  for (let i = 0; i < totalCells; i++) {
    STATIC_GRID_CONTRAST[i] = Math.abs(STATIC_GRID_L[i] - avgZoneL);
  }

  // Paso 4: Detección de Corte de Escena (Scene Cut) sin allocs
  STATIC_HIST.fill(0);
  for (let i = 0; i < totalCells; i++) {
    const bin = Math.min(15, Math.floor(STATIC_GRID_L[i] * 16));
    STATIC_HIST[bin] += 1 / totalCells;
  }

  let sceneCut = false;
  if (mem.prevHist) {
    let histDist = 0;
    for (let b = 0; b < 16; b++) {
      histDist += Math.abs(STATIC_HIST[b] - mem.prevHist[b]);
    }
    if (histDist > 0.70) {
      sceneCut = true;
    }
  }
  if (!mem.prevHist) mem.prevHist = new Float32Array(16);
  mem.prevHist.set(STATIC_HIST);

  // Paso 5: Agrupamiento Perceptual en Candidatos de Color
  resetStaticCandidateBins();
  const candidateBins = STATIC_CANDIDATE_BINS;

  for (let gy = 0; gy < gh; gy++) {
    const v = gy / gh;
    for (let gx = 0; gx < gw; gx++) {
      const u = gx / gw;
      const cellIdx = gy * gw + gx;

      const L = STATIC_GRID_L[cellIdx];
      const a = STATIC_GRID_A[cellIdx];
      const b = STATIC_GRID_B[cellIdx];
      const chroma = Math.sqrt(a * a + b * b);
      const contrast = STATIC_GRID_CONTRAST[cellIdx];
      const motion = STATIC_GRID_MOTION[cellIdx] * effectiveMotionScale;

      const spatialW = computeSpatialWeight(u, v, zoneId);
      const bbFactor = detectBlackBarFactor(v, zoneId, avgZoneL);

      const isBottomZone = zoneId === 'bottom' || zoneId === 'bottom-left' || zoneId === 'bottom-right' || v > 0.65;
      const isWhiteTextCandidate = L > 0.68 && chroma < 0.08 && contrast > 0.35;
      let textLikelihood = (isBottomZone && isWhiteTextCandidate) ? 0.85 : 0;

      const lBin = L > 0.45 ? 4 : 0;
      const cBin = (a >= 0 ? 2 : 0) + (b >= 0 ? 1 : 0);
      const binIdx = lBin + cBin;

      const c = candidateBins[binIdx];
      c.count++;
      c.sumL += L;
      c.sumA += a;
      c.sumB += b;
      c.sumContrast += contrast;
      c.sumMotion += motion;
      c.sumSpatial += spatialW;
      c.sumBlackBar *= bbFactor;
      c.sumTextLikelihood += textLikelihood;
      c.rSum += STATIC_GRID_R[cellIdx];
      c.gSum += STATIC_GRID_G[cellIdx];
      c.bSum += STATIC_GRID_B_COL[cellIdx];
    }
  }

  // Paso 6: Scoring de Candidatos y Softmax Blending
  const candidates: ColorCandidate[] = [];

  for (let i = 0; i < 8; i++) {
    const c = candidateBins[i];
    if (c.count === 0) continue;

    const count = c.count;
    const meanL = c.sumL / count;
    const meanA = c.sumA / count;
    const meanB = c.sumB / count;
    const meanChroma = Math.sqrt(meanA * meanA + meanB * meanB);
    const meanContrast = c.sumContrast / count;
    const meanMotion = c.sumMotion / count;
    const meanSpatial = c.sumSpatial / count;
    const meanTextLikelihood = c.sumTextLikelihood / count;
    const coverage = count / totalCells;

    // Saliencia Perceptual según la especificación:
    // S = f(Contrast, Luminance, Chroma, SpatialIsolation, Motion, Persistence)
    const salience = Math.min(
      1.0,
      meanContrast * 0.40 +
      meanChroma * 1.50 +
      meanL * 0.25 +
      meanMotion * 0.30
    );

    const srgb: RGB = {
      r: Math.round(c.rSum / count),
      g: Math.round(c.gSum / count),
      b: Math.round(c.bSum / count),
    };

    const textPenalty = 1.0 - lambdaText * meanTextLikelihood;

    candidates.push({
      oklab: {
        L: meanL,
        a: meanA,
        b: meanB,
        chroma: meanChroma,
        hue: Math.atan2(meanB, meanA),
      },
      srgb,
      coverage,
      saliency: Math.max(0.05, salience),
      luminance: Math.max(0.01, meanL),
      localContrast: meanContrast,
      motion: meanMotion,
      spatialWeight: meanSpatial,
      blackBarFactor: Math.max(0.05, c.sumBlackBar),
      textPenalty: Math.max(0.05, textPenalty),
      uiPenalty: 1.0,
    });
  }

  if (candidates.length === 0) {
    return { rawColor: { r: 0, g: 0, b: 0 }, brightness: 0 };
  }

  // Paso 7: Scoring de cada candidato
  // Score_k = C_k^alpha * S_k^beta * L_k^gamma * (1 + delta * M_k) * P_k * B_k * T_k
  const scores = candidates.map((cand) => {
    const sublinearCoverage = Math.pow(cand.coverage, alphaCov);
    const saliencyFactor = Math.pow(cand.saliency, betaSal);
    const luminanceFactor = Math.pow(cand.luminance, 0.50);
    const motionFactor = 1.0 + deltaMot * cand.motion;
    const spatialFactor = cand.spatialWeight;
    const contextFactor = cand.blackBarFactor * cand.textPenalty;

    const rawScore =
      sublinearCoverage *
      saliencyFactor *
      luminanceFactor *
      motionFactor *
      spatialFactor *
      contextFactor;

    return Math.max(1e-5, rawScore);
  });

  // Softmax con Temperatura τ
  let maxScore = -Infinity;
  for (let s of scores) {
    if (s > maxScore) maxScore = s;
  }

  let expSum = 0;
  const weights = scores.map((s) => {
    // Normalizar relativo al máximo para estabilidad numérica
    const expVal = Math.exp((s / (maxScore + 1e-6)) * tauTemp);
    expSum += expVal;
    return expVal;
  });

  for (let i = 0; i < weights.length; i++) {
    weights[i] /= expSum || 1;
  }

  // Mezcla de candidatos en espacio OKLab
  let blendL = 0;
  let blendA = 0;
  let blendB = 0;

  for (let i = 0; i < candidates.length; i++) {
    const w = weights[i];
    const cand = candidates[i];
    blendL += cand.oklab.L * w;
    blendA += cand.oklab.a * w;
    blendB += cand.oklab.b * w;
  }

  const targetRgb = oklabToRgb(blendL, blendA, blendB);

  // Paso 8: Filtro Temporal Adaptativo (Adaptive Temporal Filter)
  // Escena estable -> suavizado alto
  // Evento rápido o explosión -> reacción rápida
  // Corte de escena -> reacción inmediata (0 smoothing)
  // Pantalla NEGRA (blendL < 0.04) -> apagado inmediato, sin lerp de 15 frames
  let alphaSmooth = 0.22; // Suave normal

  // FAST-PATH: Si la escena está prácticamente negra en OKLab, forzar salida a 0
  // inmediatamente. Sin este fast-path, el suavizado mantiene el foco encendido
  // durante ~15 frames después de que la pantalla se pone negra.
  const isNearBlack = blendL < 0.04 && avgZoneMotion < 0.05;
  if (isNearBlack) {
    mem.outputR = 0;
    mem.outputG = 0;
    mem.outputB = 0;
    mem.outputL = 0;
    // No avanzar framesActive: el primer frame con color tras oscuridad también reacciona rápido
  } else if (mem.framesActive === 0 || sceneCut) {
    alphaSmooth = 1.0;
    mem.outputR = targetRgb.r;
    mem.outputG = targetRgb.g;
    mem.outputB = targetRgb.b;
    mem.outputL = blendL;
  } else {
    if (avgZoneMotion > 0.25) {
      alphaSmooth = 0.70; // Reacción rápida para destellos y explosiones
    } else if (avgZoneMotion > 0.10) {
      alphaSmooth = 0.45; // Moderado
    }
    mem.outputR += (targetRgb.r - mem.outputR) * alphaSmooth;
    mem.outputG += (targetRgb.g - mem.outputG) * alphaSmooth;
    mem.outputB += (targetRgb.b - mem.outputB) * alphaSmooth;
    mem.outputL += (blendL - mem.outputL) * alphaSmooth;
  }

  mem.framesActive++;

  // Guardar cuadrícula para el cálculo de movimiento en el siguiente frame (sin allocs)
  if (!mem.prevGridL || mem.prevGridL.length < totalCells) {
    mem.prevGridL = new Float32Array(totalCells);
    mem.prevGridA = new Float32Array(totalCells);
    mem.prevGridB = new Float32Array(totalCells);
  }
  mem.prevGridL.set(STATIC_GRID_L.subarray(0, totalCells));
  mem.prevGridA.set(STATIC_GRID_A.subarray(0, totalCells));
  mem.prevGridB.set(STATIC_GRID_B.subarray(0, totalCells));
  mem.gridW = gw;
  mem.gridH = gh;

  const finalColor: RGB = {
    r: Math.max(0, Math.min(255, Math.round(mem.outputR))),
    g: Math.max(0, Math.min(255, Math.round(mem.outputG))),
    b: Math.max(0, Math.min(255, Math.round(mem.outputB))),
  };

  const brightnessPercent = Math.round(Math.max(0, Math.min(100, mem.outputL * 100)));

  const info = `OKLab L:${(mem.outputL * 100).toFixed(0)}% · Saliencia: ${(avgZoneMotion > 0.2 ? '⚡ Alto' : '🍃 Estable')}`;

  return {
    rawColor: finalColor,
    brightness: brightnessPercent,
    info,
  };
}
